import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ScenarioCustomDataPanel } from '../components/ScenarioCustomDataPanel.js';
import { ScenarioNotesPanel } from '../components/ScenarioNotesPanel.js';
import { useScenarioDetail, useUpdateScenario } from '../hooks.js';

/**
 * Scenario detail. Phase B real implementation will surface tabs
 * (overview / executions / KPI rollup / notes / AI summary). Today
 * dumps the engine response so the read path is exercised.
 */
export function ScenarioDetailPage(): JSX.Element {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const { data, isLoading, error } = useScenarioDetail(scenarioId);
  const { update, isPending: renaming } = useUpdateScenario(scenarioId);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState('');

  const currentName = (data?.name as string | undefined) ?? '';

  async function saveRename(): Promise<void> {
    const next = draftName.trim();
    if (!next || next === currentName) {
      setEditing(false);
      return;
    }
    await update({ name: next });
    setEditing(false);
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Link
        to="/scenarios"
        className="mb-3 inline-block text-xs text-blue-600 hover:underline dark:text-blue-400"
      >
        ← Back to list
      </Link>
      <div className="mb-1 flex items-center gap-3">
        {editing ? (
          <>
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              autoFocus
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-lg font-semibold text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => void saveRename()}
              disabled={renaming}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              {renaming ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-gray-500 hover:underline dark:text-gray-400"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {currentName ? (
                currentName
              ) : (
                <>
                  Scenario <span className="font-mono text-base text-gray-500">{scenarioId}</span>
                </>
              )}
            </h1>
            {data ? (
              <button
                type="button"
                onClick={() => {
                  setDraftName(currentName);
                  setEditing(true);
                }}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Rename
              </button>
            ) : null}
          </>
        )}
      </div>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Per-scenario engine snapshot. Notes panel below; tabbed surface arrives in Phase B.
      </p>
      {isLoading ? (
        <div className="rounded-md border border-gray-200 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          Loading…
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          <p className="mb-1 font-semibold">Could not load scenario</p>
          <p className="text-xs">{error.message}</p>
        </div>
      ) : (
        <pre className="overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-4 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
      {scenarioId ? (
        <>
          <ScenarioCustomDataPanel
            scenarioId={scenarioId}
            initialCustomData={data ? extractCustomData(data) : null}
          />
          <ScenarioNotesPanel scenarioId={scenarioId} />
        </>
      ) : null}
    </div>
  );
}

function extractCustomData(scenario: Record<string, unknown>): unknown {
  // Engine response shape varies — try common keys.
  return (
    (scenario.customData as unknown) ??
    (scenario.custom_data as unknown) ??
    (scenario.customFieldsData as unknown) ??
    null
  );
}
